import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  AppAutocomplete,
  AppButton,
  AppCheckbox,
  AppColorField,
  AppIconButton,
  AppTextField,
} from "../../../components/form";
import {
  AppFormField,
  AppFormGrid,
  AppSlideOver,
  PageSectionCard,
} from "../../../components/layout";
import PageEmptyState from "../../../components/Page/PageEmptyState";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import { isConfirmCancelled, useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import {
  EMBED_FEATURE_KEYS,
  EMBED_ROLES,
  embedConfigApi,
  type EmbedAllowance,
  type EmbedConfig,
  type EmbedScope,
  type EmbedUsage,
  type EmbedConfigPayload,
  type EmbedFeatures,
  type EmbedPosition,
  type EmbedRole,
  type EmbedThemeMode,
} from "../../../services/embedConfigService";
import { workspaceService, type WorkspaceSummary } from "../../../services/workspaceService";
import { buildEmbedSnippet, buildHostCspHint, parseOriginList } from "../../../utils/embedSnippet";

const PLATFORM_WORKSPACE = "__platform__";
const DEFAULT_GRACE_DAYS = 7;

type FormState = {
  id?: string;
  name: string;
  enabled: boolean;
  workspaceId: string;
  originsText: string;
  primaryColor: string;
  mode: EmbedThemeMode;
  position: EmbedPosition;
  launcherLabel: string;
  welcomeMessage: string;
  locale: string;
  features: EmbedFeatures;
  rememberSession: boolean;
  allowedRoles: EmbedRole[];
  passwordLogin: boolean;
  googleLogin: boolean;
  ticketLogin: boolean;
  perUser: number;
  perKey: number;
  lockedBySuperAdmin: boolean;
};

const defaultFeatures = (): EmbedFeatures => ({
  saveWidget: true,
  addToDashboard: true,
  mutations: true,
  export: true,
  warehouseJobs: true,
  forecast: true,
  showHistory: true,
  scopePickers: true,
  hostContext: false,
  pageContent: false,
  formFill: false,
});

const emptyForm = (): FormState => ({
  name: "",
  enabled: true,
  workspaceId: PLATFORM_WORKSPACE,
  originsText: "",
  primaryColor: "",
  mode: "AUTO",
  position: "BOTTOM_RIGHT",
  launcherLabel: "",
  welcomeMessage: "",
  locale: "",
  features: defaultFeatures(),
  rememberSession: true,
  allowedRoles: [...EMBED_ROLES],
  passwordLogin: true,
  googleLogin: true,
  ticketLogin: false,
  perUser: 20,
  perKey: 600,
  lockedBySuperAdmin: false,
});

function toForm(row: EmbedConfig): FormState {
  return {
    id: row.id,
    name: row.name || "",
    enabled: row.enabled,
    workspaceId: row.workspaceId || PLATFORM_WORKSPACE,
    originsText: (row.allowedOrigins || []).join("\n"),
    primaryColor: row.theme?.primaryColor || "",
    mode: row.theme?.mode || "AUTO",
    position: row.theme?.position || "BOTTOM_RIGHT",
    launcherLabel: row.theme?.launcherLabel || "",
    welcomeMessage: row.theme?.welcomeMessage || "",
    locale: row.theme?.locale || "",
    features: { ...defaultFeatures(), ...(row.features || {}) },
    rememberSession: row.rememberSession !== false,
    allowedRoles: row.allowedRoles?.length ? row.allowedRoles : [...EMBED_ROLES],
    passwordLogin: row.authProviders?.password !== false,
    googleLogin: row.authProviders?.google !== false,
    ticketLogin: row.authProviders?.ticket === true,
    perUser: row.rateLimit?.messagesPerUserPerMinute ?? 20,
    perKey: row.rateLimit?.messagesPerKeyPerMinute ?? 600,
    lockedBySuperAdmin: row.lockedBySuperAdmin === true,
  };
}

function toPayload(form: FormState, existing?: EmbedConfig): EmbedConfigPayload {
  return {
    name: form.name.trim(),
    enabled: form.enabled,
    workspaceId: form.workspaceId === PLATFORM_WORKSPACE ? null : form.workspaceId,
    allowedOrigins: parseOriginList(form.originsText),
    theme: {
      primaryColor: form.primaryColor.trim() || null,
      mode: form.mode,
      position: form.position,
      launcherLabel: form.launcherLabel.trim() || null,
      welcomeMessage: form.welcomeMessage.trim() || null,
      locale: form.locale.trim() || null,
    },
    defaultScope: existing?.defaultScope || {
      indices: [],
      adaptorIds: [],
      openApiCatalogIds: [],
      allowedSlashCommands: [],
    },
    features: form.features,
    rememberSession: form.rememberSession,
    allowedRoles: form.allowedRoles,
    authProviders: {
      password: form.passwordLogin,
      google: form.googleLogin,
      // Integration tickets admit users of any workspace, so only platform-wide embeds can use them.
      ticket: form.ticketLogin && form.workspaceId === PLATFORM_WORKSPACE,
    },
    rateLimit: { messagesPerUserPerMinute: form.perUser, messagesPerKeyPerMinute: form.perKey },
    lockedBySuperAdmin: form.lockedBySuperAdmin,
  };
}

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const monoSx = { fontFamily: "monospace", wordBreak: "break-all" } as const;

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={650}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

/** Last-30-days usage of one embed: totals and messages per day. */
function UsageView({ usage }: { usage: EmbedUsage }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...usage.daily.map((d) => d.messages));
  const tiles: [string, number][] = [
    [t("superAdmin.embeds.usageMessages"), usage.messages],
    [t("superAdmin.embeds.usageConversations"), usage.conversations],
    [t("superAdmin.embeds.usageUsers"), usage.activeUsers],
    [t("superAdmin.embeds.usageLogins"), usage.logins],
    [t("superAdmin.embeds.usageDenied"), usage.deniedLogins],
  ];
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        {tiles.map(([label, value]) => (
          <Box
            key={label}
            sx={{ minWidth: 120, px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 1 }}
          >
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h6">{value.toLocaleString()}</Typography>
          </Box>
        ))}
      </Stack>
      {usage.daily.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("superAdmin.embeds.usageEmpty")}
        </Typography>
      ) : (
        <Stack spacing={0.5} aria-label={t("superAdmin.embeds.usageDaily")}>
          {usage.daily.map((d) => (
            <Stack key={d.date} direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ width: 84, fontVariantNumeric: "tabular-nums" }}>
                {d.date}
              </Typography>
              <Box sx={{ flex: 1, maxWidth: 360 }}>
                <Box
                  sx={{
                    height: 10,
                    width: `${(d.messages / max) * 100}%`,
                    minWidth: 2,
                    bgcolor: "primary.main",
                    borderRadius: 0.5,
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ width: 120, fontVariantNumeric: "tabular-nums" }}>
                {t("superAdmin.embeds.usageDayValue", { messages: d.messages, users: d.activeUsers })}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Embedded chat management. "platform" = Super Admin (all embeds, any workspace, lock flag);
 * "workspace" = workspace admin (own workspace only, within the package allowance, locked rows read-only).
 */
export default function EmbedsPanel({ scope = "platform" }: { scope?: EmbedScope }) {
  const { t } = useTranslation();
  const platform = scope === "platform";
  const api = useMemo(() => embedConfigApi(scope), [scope]);
  const [allowance, setAllowance] = useState<EmbedAllowance | null>(null);
  const [usageId, setUsageId] = useState<string | null>(null);
  const [usage, setUsage] = useState<EmbedUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const confirm = useConfirm();
  const { confirmDelete } = useDeleteConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rows, setRows] = useState<EmbedConfig[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = useMemo(() => rows.find((r) => r.id === selectedId) || null, [rows, selectedId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, ws, allow] = await Promise.all([
        api.list(),
        platform ? workspaceService.listAll().catch(() => [] as WorkspaceSummary[]) : Promise.resolve([]),
        platform ? Promise.resolve(null) : api.allowance(),
      ]);
      setRows(Array.isArray(list) ? list : []);
      setWorkspaces(Array.isArray(ws) ? ws : []);
      setAllowance(allow);
    } catch {
      setError(t("superAdmin.embeds.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t, api, platform]);

  useEffect(() => {
    void load();
  }, [load]);

  const showUsage = async (row: EmbedConfig) => {
    setUsageId(row.id);
    setUsage(null);
    setUsageLoading(true);
    try {
      setUsage(await api.usage(row.id, 30));
    } catch {
      setError(t("superAdmin.embeds.usageError"));
    } finally {
      setUsageLoading(false);
    }
  };
  const usageRow = rows.find((r) => r.id === usageId) || null;

  const flash = (message: string) => {
    setError(null);
    setSuccess(message);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(emptyForm());
  };

  const startCreate = () => {
    setForm(emptyForm());
    setFormOpen(true);
    setError(null);
  };

  const startEdit = (row: EmbedConfig) => {
    setForm(toForm(row));
    setSelectedId(row.id);
    setFormOpen(true);
    setError(null);
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const existing = rows.find((r) => r.id === form.id);
      const saved = form.id
        ? await api.update(form.id, toPayload(form, existing))
        : await api.create(toPayload(form));
      flash(t("superAdmin.embeds.saveSuccess"));
      setFormOpen(false);
      setForm(emptyForm());
      setSelectedId(saved.id);
      await load();
    } catch {
      setError(t("superAdmin.embeds.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const onRotate = async (row: EmbedConfig) => {
    try {
      await confirm({
        title: t("superAdmin.embeds.rotateTitle", { name: row.name }),
        description: t("superAdmin.embeds.rotateDescription", { days: DEFAULT_GRACE_DAYS }),
        confirmationText: t("superAdmin.embeds.rotate"),
        cancellationText: t("common.cancel"),
      });
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      throw e;
    }
    try {
      const rotated = await api.rotate(row.id, DEFAULT_GRACE_DAYS);
      setSelectedId(rotated.id);
      flash(t("superAdmin.embeds.rotateSuccess"));
      await load();
    } catch {
      setError(t("superAdmin.embeds.rotateError"));
    }
  };

  const onDelete = async (row: EmbedConfig) => {
    try {
      await confirmDelete({ itemName: row.name });
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      throw e;
    }
    try {
      await api.remove(row.id);
      if (form.id === row.id) closeForm();
      if (selectedId === row.id) setSelectedId(null);
      if (usageId === row.id) {
        setUsageId(null);
        setUsage(null);
      }
      flash(t("superAdmin.embeds.deleteSuccess"));
      await load();
    } catch {
      setError(t("superAdmin.embeds.deleteError"));
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      flash(t("superAdmin.embeds.copied"));
    } catch {
      setError(t("superAdmin.embeds.copyError"));
    }
  };

  const toggleRole = (role: EmbedRole, checked: boolean) =>
    setForm((f) => ({
      ...f,
      allowedRoles: checked
        ? EMBED_ROLES.filter((r) => r === role || f.allowedRoles.includes(r))
        : f.allowedRoles.filter((r) => r !== role),
    }));

  const canCreate =
    platform || (allowance?.allowed === true && (allowance.max == null || allowance.used < allowance.max));

  const formInvalid =
    !form.name.trim() ||
    parseOriginList(form.originsText).length === 0 ||
    form.allowedRoles.length === 0 ||
    (!form.passwordLogin && !form.googleLogin && !(form.ticketLogin && form.workspaceId === PLATFORM_WORKSPACE));

  if (loading) {
    return (
      <Stack alignItems="center" py={4} sx={{ width: "100%" }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {error && !formOpen && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <PageSectionCard
        title={t("superAdmin.embeds.listTitle")}
        description={
          platform ? t("superAdmin.embeds.description") : t("superAdmin.embeds.workspaceDescription")
        }
        actions={
          canCreate ? (
            <AppButton
              size="small"
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={startCreate}
            >
              {t("superAdmin.embeds.create")}
            </AppButton>
          ) : undefined
        }
        noPadding={rows.length > 0}
      >
        {!platform && allowance && (
          <Alert
            severity={allowance.allowed ? "info" : "warning"}
            sx={{ mx: rows.length > 0 ? 1.5 : 0, mt: rows.length > 0 ? 1.5 : 0, mb: 1.5 }}
          >
            {!allowance.allowed
              ? t("superAdmin.embeds.allowanceNone")
              : allowance.max == null
                ? t("superAdmin.embeds.allowanceUnlimited", { used: allowance.used })
                : t("superAdmin.embeds.allowanceCapped", { used: allowance.used, max: allowance.max })}
          </Alert>
        )}
        {rows.length === 0 ? (
          <PageEmptyState
            message={t("superAdmin.embeds.empty")}
            actionLabel={canCreate ? t("superAdmin.embeds.create") : undefined}
            onAction={canCreate ? startCreate : undefined}
            actionIcon={canCreate ? <AddOutlinedIcon /> : undefined}
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("superAdmin.embeds.name")}</TableCell>
                {platform && <TableCell>{t("superAdmin.embeds.workspace")}</TableCell>}
                <TableCell>{t("superAdmin.embeds.origins")}</TableCell>
                <TableCell>{t("superAdmin.embeds.status")}</TableCell>
                <TableCell>{t("superAdmin.embeds.publicKey")}</TableCell>
                <TableCell align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover selected={selectedId === row.id}>
                  <TableCell>{row.name}</TableCell>
                  {platform && (
                    <TableCell>
                      {row.workspaceId
                        ? row.workspaceName || row.workspaceId
                        : t("superAdmin.embeds.platformWorkspace")}
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="caption" sx={monoSx}>
                      {row.allowedOrigins.join(", ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={row.enabled ? "success" : "default"}
                      label={
                        row.enabled
                          ? t("superAdmin.embeds.enabledShort")
                          : t("superAdmin.embeds.disabledShort")
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={monoSx}>
                      {row.publicKey}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                      <AppIconButton
                        size="small"
                        aria-label={t("superAdmin.embeds.snippet")}
                        title={t("superAdmin.embeds.snippet")}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <CodeOutlinedIcon fontSize="small" />
                      </AppIconButton>
                      <AppIconButton
                        size="small"
                        aria-label={t("superAdmin.embeds.usage")}
                        title={t("superAdmin.embeds.usage")}
                        onClick={() => void showUsage(row)}
                      >
                        <QueryStatsOutlinedIcon fontSize="small" />
                      </AppIconButton>
                      {!platform && row.lockedBySuperAdmin ? (
                        <AppIconButton
                          size="small"
                          disabled
                          aria-label={t("superAdmin.embeds.lockedChip")}
                          title={t("superAdmin.embeds.lockedChip")}
                        >
                          <LockOutlinedIcon fontSize="small" />
                        </AppIconButton>
                      ) : (
                        <>
                          <AppIconButton
                            size="small"
                            aria-label={t("common.edit")}
                            title={t("common.edit")}
                            onClick={() => startEdit(row)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </AppIconButton>
                          <AppIconButton
                            size="small"
                            aria-label={t("superAdmin.embeds.rotate")}
                            title={t("superAdmin.embeds.rotate")}
                            onClick={() => void onRotate(row)}
                          >
                            <AutorenewOutlinedIcon fontSize="small" />
                          </AppIconButton>
                          <AppIconButton
                            size="small"
                            color="error"
                            aria-label={t("common.delete")}
                            title={t("common.delete")}
                            onClick={() => void onDelete(row)}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSectionCard>

      {selected && (
        <PageSectionCard
          title={t("superAdmin.embeds.snippetTitle", { name: selected.name })}
          description={t("superAdmin.embeds.snippetDescription")}
        >
          <Stack spacing={2}>
            {!selected.enabled && (
              <Alert severity="warning">{t("superAdmin.embeds.disabledWarning")}</Alert>
            )}
            <Box
              component="pre"
              sx={{
                ...monoSx,
                m: 0,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
                fontSize: 12,
                whiteSpace: "pre-wrap",
              }}
            >
              {buildEmbedSnippet(selected.loaderUrl, selected.publicKey)}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <AppButton
                onClick={() => void copy(buildEmbedSnippet(selected.loaderUrl, selected.publicKey))}
              >
                {t("superAdmin.embeds.copySnippet")}
              </AppButton>
              <AppButton variant="outlined" onClick={() => void copy(selected.publicKey)}>
                {t("superAdmin.embeds.copyKey")}
              </AppButton>
            </Stack>
            <Typography variant="body2">{t("superAdmin.embeds.cspHint")}</Typography>
            <Typography variant="caption" sx={monoSx}>
              {buildHostCspHint(selected.appOrigin)}
            </Typography>
            {selected.previousKey && (
              <Alert severity="info">
                {t("superAdmin.embeds.previousKeyInfo", {
                  key: selected.previousKey,
                  until: formatDate(selected.previousKeyGraceUntil),
                })}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary">
              {t("superAdmin.embeds.loaderPending")}
            </Typography>
          </Stack>
        </PageSectionCard>
      )}

      {usageRow && (
        <PageSectionCard
          title={t("superAdmin.embeds.usageTitle", { name: usageRow.name })}
          description={t("superAdmin.embeds.usageDescription")}
        >
          {usageLoading || !usage ? <CircularProgress size={24} /> : <UsageView usage={usage} />}
        </PageSectionCard>
      )}

      <AppSlideOver
        open={formOpen}
        onClose={closeForm}
        size="lg"
        title={form.id ? t("superAdmin.embeds.editTitle") : t("superAdmin.embeds.createTitle")}
        actions={
          <>
            <AppButton variant="outlined" onClick={closeForm} disabled={saving}>
              {t("common.cancel")}
            </AppButton>
            <AppButton onClick={() => void onSave()} disabled={saving || formInvalid} loading={saving}>
              {form.id ? t("common.save") : t("common.create")}
            </AppButton>
          </>
        }
      >
        {error && formOpen && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={2.5} divider={<Divider flexItem />}>
          <FormSection title={t("superAdmin.embeds.basicsSection")}>
            <AppFormGrid>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("superAdmin.embeds.name")}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  autoFocus={!form.id}
                  fullWidth
                />
              </AppFormField>
              {platform && (
                <AppFormField size={GRID_FULL}>
                  <AppAutocomplete<{ id: string; name: string }>
                    label={t("superAdmin.embeds.workspace")}
                    options={[
                      { id: PLATFORM_WORKSPACE, name: t("superAdmin.embeds.platformWorkspace") },
                      ...workspaces.map((w) => ({ id: w.id, name: w.name })),
                    ]}
                    value={
                      form.workspaceId === PLATFORM_WORKSPACE
                        ? { id: PLATFORM_WORKSPACE, name: t("superAdmin.embeds.platformWorkspace") }
                        : workspaces
                            .filter((w) => w.id === form.workspaceId)
                            .map((w) => ({ id: w.id, name: w.name }))[0] ?? null
                    }
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, value) =>
                      setForm((f) => ({ ...f, workspaceId: value?.id ?? PLATFORM_WORKSPACE }))
                    }
                    helperText={t("superAdmin.embeds.workspaceHelp")}
                  />
                </AppFormField>
              )}
              <AppFormField size={GRID_HALF}>
                <AppCheckbox
                  label={t("superAdmin.embeds.enabled")}
                  checked={form.enabled}
                  onChange={(_, checked) => setForm((f) => ({ ...f, enabled: checked }))}
                />
              </AppFormField>
              {platform ? (
                <AppFormField size={GRID_HALF}>
                  <AppCheckbox
                    label={t("superAdmin.embeds.locked")}
                    checked={form.lockedBySuperAdmin}
                    onChange={(_, checked) => setForm((f) => ({ ...f, lockedBySuperAdmin: checked }))}
                  />
                </AppFormField>
              ) : null}
            </AppFormGrid>
          </FormSection>

          <FormSection title={t("superAdmin.embeds.hostsSection")}>
            <AppTextField
              label={t("superAdmin.embeds.origins")}
              value={form.originsText}
              onChange={(e) => setForm((f) => ({ ...f, originsText: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              placeholder={"https://erp.example.com\nhttps://*.example.com"}
              helperText={t("superAdmin.embeds.originsHelp")}
            />
          </FormSection>

          <FormSection title={t("superAdmin.embeds.themeSection")}>
            <AppFormGrid>
              <AppFormField size={GRID_FULL}>
                <AppColorField
                  label={t("superAdmin.embeds.primaryColor")}
                  value={form.primaryColor}
                  onChange={(value) => setForm((f) => ({ ...f, primaryColor: value }))}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppAutocomplete<EmbedThemeMode>
                  label={t("superAdmin.embeds.mode")}
                  options={["AUTO", "LIGHT", "DARK"]}
                  value={form.mode}
                  disableClearable
                  getOptionLabel={(m) => t(`superAdmin.embeds.modes.${m}`)}
                  onChange={(_, value) => value && setForm((f) => ({ ...f, mode: value }))}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppAutocomplete<EmbedPosition>
                  label={t("superAdmin.embeds.position")}
                  options={["BOTTOM_RIGHT", "BOTTOM_LEFT"]}
                  value={form.position}
                  disableClearable
                  getOptionLabel={(p) => t(`superAdmin.embeds.positions.${p}`)}
                  onChange={(_, value) => value && setForm((f) => ({ ...f, position: value }))}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("superAdmin.embeds.locale")}
                  value={form.locale}
                  onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value }))}
                  placeholder="tr"
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("superAdmin.embeds.launcherLabel")}
                  value={form.launcherLabel}
                  onChange={(e) => setForm((f) => ({ ...f, launcherLabel: e.target.value }))}
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("superAdmin.embeds.welcomeMessage")}
                  value={form.welcomeMessage}
                  onChange={(e) => setForm((f) => ({ ...f, welcomeMessage: e.target.value }))}
                  multiline
                  minRows={2}
                />
              </AppFormField>
            </AppFormGrid>
          </FormSection>

          <FormSection title={t("superAdmin.embeds.featuresSection")}>
            <AppFormGrid spacing="compact">
              {EMBED_FEATURE_KEYS.map((key) => (
                <AppFormField key={key} size={GRID_HALF}>
                  <AppCheckbox
                    label={t(`superAdmin.embeds.features.${key}`)}
                    checked={form.features[key]}
                    onChange={(_, checked) =>
                      setForm((f) => ({ ...f, features: { ...f.features, [key]: checked } }))
                    }
                  />
                </AppFormField>
              ))}
            </AppFormGrid>
          </FormSection>

          <FormSection title={t("superAdmin.embeds.accessSection")}>
            <AppFormGrid spacing="compact">
              {EMBED_ROLES.map((role) => (
                <AppFormField key={role} size={GRID_HALF}>
                  <AppCheckbox
                    label={t(`superAdmin.embeds.roles.${role}`)}
                    checked={form.allowedRoles.includes(role)}
                    onChange={(_, checked) => toggleRole(role, checked)}
                  />
                </AppFormField>
              ))}
              <AppFormField size={GRID_HALF}>
                <AppCheckbox
                  label={t("superAdmin.embeds.passwordLogin")}
                  checked={form.passwordLogin}
                  onChange={(_, checked) => setForm((f) => ({ ...f, passwordLogin: checked }))}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppCheckbox
                  label={t("superAdmin.embeds.googleLogin")}
                  checked={form.googleLogin}
                  onChange={(_, checked) => setForm((f) => ({ ...f, googleLogin: checked }))}
                />
              </AppFormField>
              {platform && (
                <AppFormField size={GRID_FULL}>
                  <AppCheckbox
                    label={t("superAdmin.embeds.ticketLogin")}
                    checked={form.ticketLogin && form.workspaceId === PLATFORM_WORKSPACE}
                    disabled={form.workspaceId !== PLATFORM_WORKSPACE}
                    onChange={(_, checked) => setForm((f) => ({ ...f, ticketLogin: checked }))}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    {form.workspaceId === PLATFORM_WORKSPACE
                      ? t("superAdmin.embeds.ticketLoginHint")
                      : t("superAdmin.embeds.ticketLoginNeedsPlatform")}
                  </Typography>
                </AppFormField>
              )}
              <AppFormField size={GRID_FULL}>
                <AppCheckbox
                  label={t("superAdmin.embeds.rememberSession")}
                  checked={form.rememberSession}
                  onChange={(_, checked) => setForm((f) => ({ ...f, rememberSession: checked }))}
                />
              </AppFormField>
            </AppFormGrid>
          </FormSection>

          <FormSection title={t("superAdmin.embeds.limitsSection")}>
            <AppFormGrid>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("superAdmin.embeds.perUser")}
                  type="number"
                  value={String(form.perUser)}
                  onChange={(e) => setForm((f) => ({ ...f, perUser: Number(e.target.value) || 0 }))}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("superAdmin.embeds.perKey")}
                  type="number"
                  value={String(form.perKey)}
                  onChange={(e) => setForm((f) => ({ ...f, perKey: Number(e.target.value) || 0 }))}
                />
              </AppFormField>
            </AppFormGrid>
          </FormSection>
        </Stack>
      </AppSlideOver>
    </Stack>
  );
}
